import { z } from "zod";
import { businessDate, DEFAULT_TIME_ZONE, localParts } from "./dates";
import { timeOfDaySchema } from "./restaurant";

/**
 * The owner's daily summary push (P11-T08): the day's sales, bills, average
 * bill and top item, sent to the owner's devices at a time they choose, in
 * the restaurant's time zone. A setting of the owner's membership, so an
 * owner of two restaurants chooses for each. See
 * docs/architecture/realtime-notifications.md, Daily summary.
 */

export const DAILY_SUMMARY_DEFAULT_TIME = "23:00";

/**
 * Which business date a summary covers: the one in progress 12 hours before
 * the chosen time. A time from 12:00 to 03:59 sends that day's figures so far
 * (a restaurant that closes late picks 23:30 or 01:00); a time from 04:00 to
 * 11:59 sends the day before, which ended at 04:00.
 */
export const DAILY_SUMMARY_SHIFT_HOURS = 12;

/** A summary the worker could not send at its time (a restart) is still sent this many minutes late, never later. */
export const DAILY_SUMMARY_GRACE_MINUTES = 120;

/** `staff.reports.dailySummary` and `setDailySummary`: on or off, and when ("HH:MM", 24 hour). */
export const dailySummarySettingsSchema = z.object({
	enabled: z.boolean(),
	time: timeOfDaySchema,
});
export type DailySummarySettings = z.infer<typeof dailySummarySettingsSchema>;

/** The business date the summary sent at `instant` covers (`DAILY_SUMMARY_SHIFT_HOURS`). */
export function dailySummaryDate(instant: Date, timeZone: string = DEFAULT_TIME_ZONE): string {
	return businessDate(instant, timeZone || DEFAULT_TIME_ZONE, DAILY_SUMMARY_SHIFT_HOURS);
}

/** Whether a summary sent at `time` covers the day before (04:00 to 11:59) rather than that day. */
export function dailySummaryCoversDayBefore(time: string): boolean {
	return time >= "04:00" && time < "12:00";
}

const minutesOf = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
const SHIFT_MINUTES = DAILY_SUMMARY_SHIFT_HOURS * 60;

/**
 * The business date whose summary is due at `now`, or null. Due from the
 * chosen time on the wall clock of the restaurant's time zone for
 * `DAILY_SUMMARY_GRACE_MINUTES`, unless that date was sent already
 * (`sentFor`, the last date sent: one summary per business date). Wall clock
 * arithmetic, so a zone with daylight saving still gets one a day.
 */
export function dailySummaryDue(
	now: Date,
	settings: { time: string; timeZone: string; sentFor: string | null },
): string | null {
	const timeZone = settings.timeZone || DEFAULT_TIME_ZONE;
	const date = dailySummaryDate(now, timeZone);
	if (settings.sentFor !== null && settings.sentFor >= date) return null;
	// Minutes since 12:00 on `date`, the start of the summary's own day.
	const local = localParts(now, timeZone);
	const nowMinutes = minutesOf(local.time) - SHIFT_MINUTES + (local.date === date ? 0 : 24 * 60);
	const chosen = minutesOf(settings.time);
	const dueMinutes =
		chosen >= SHIFT_MINUTES ? chosen - SHIFT_MINUTES : chosen + 24 * 60 - SHIFT_MINUTES;
	if (nowMinutes < dueMinutes || nowMinutes >= dueMinutes + DAILY_SUMMARY_GRACE_MINUTES)
		return null;
	return date;
}
